import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Subject, Observable, Subscription } from 'rxjs';
import { Directive, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Api } from '../../../../../../shared/constants/api';
import { $workspace, CHAR_LIMITS } from '../../../../../../shared/constants/workspace';
import { WorkspaceService } from '../../../../services/workspace.service';
import { CustomValidators } from '../../../../services/helpers/form-custom-validators';
import { IWorkspace } from '../../../../../../shared/interfaces/workspace.interface';
import { NotificationPosition } from '../../../../models/notification.model';
import { LoadErrorHandler } from '../../../../services/load-error-handler';
import { TranslateService } from '../../../../translate/translate.service';
import { NotificationService } from '../../../../services/notification.service';
import { TenantRouteUtil } from '../../tenant-route.util';
import { CommonUtility } from '../../../../utility/common.utility';
import { errorMessagesNames } from '../../../../../../shared/constants/errors';

@Directive()
export abstract class UpsertTenantWorkspaceComponent implements OnInit, OnDestroy {
  public Api: typeof Api;
  public $workspace: typeof $workspace;
  public CHAR_LIMITS: Readonly<Partial<Record<$workspace, number>>>;

  public form: UntypedFormGroup;
  public tenantId: string;
  public formValidationRequest$: Subject<void>;
  public readonly tenantRoutePath: string;

  protected submitForm$: Subject<void>;
  protected subscriptions: Subscription;

  protected constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected loadErrorHandler: LoadErrorHandler,
    protected translateService: TranslateService,
    protected workspaceService: WorkspaceService,
    protected notificationService: NotificationService,
  ) {
    this.Api = Api;
    this.$workspace = $workspace;
    this.tenantId = this.activatedRoute.snapshot.params[Api.tenantId];
    this.CHAR_LIMITS = CHAR_LIMITS;

    this.form = this.formBuilder.group({
      [$workspace.name]: this.formBuilder.control('', [
        CustomValidators.required,
        Validators.maxLength(CHAR_LIMITS[$workspace.name]),
      ]),
      [$workspace.description]: this.formBuilder.control('', [
        CustomValidators.required,
        Validators.maxLength(CHAR_LIMITS[$workspace.description]),
      ]),
    });
    this.formValidationRequest$ = new Subject<void>();

    this.submitForm$ = new Subject<void>();
    this.subscriptions = new Subscription();
    this.tenantRoutePath = TenantRouteUtil.getTenantRoutePath(this.router.url);
  }

  protected abstract sendRequest$(): Observable<IWorkspace>;

  public abstract get title(): string;

  public abstract get submitButtonLabel(): string;

  public abstract get submissionSuccessMessage(): string;

  public abstract get submissionFailureMessage(): string;

  public onSubmit(): void {
    this.formValidationRequest$.next();
    if (this.form.valid) {
      this.submitForm$.next();
    }
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.sendRequest$().subscribe({
        next: async () => {
          this.notificationService.success(this.submissionSuccessMessage, NotificationPosition.Toast, true);
          return this.router.navigate([this.tenantRoutePath, this.tenantId]);
        },
        error: (err: HttpErrorResponse) => {
          if (CommonUtility.extractHTTPErrorName(err) === errorMessagesNames.DigitalPropertyUsageLimitReached) {
            this.loadErrorHandler.handleError(
              this.translateService.instant('digital_property_usage_limit_reached'),
              err,
              UpsertTenantWorkspaceComponent.name,
            );
          } else {
            this.loadErrorHandler.handleError(this.submissionFailureMessage, err, UpsertTenantWorkspaceComponent.name);
          }
        },
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
